import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
  PieChart,
  Pie,
} from "recharts";
import { SECTOR_LABELS } from "../../data/mockData";
import { useAuth } from "../../context/authContext";
import { ROLES } from "../../constants/roles";
import { useFunderFinancialHook } from "../../hooks/useFunderHooks";
import {
  Card,
  PageHeader,
  CHART_COLORS,
} from "../fundercomponents/funderComponents";
import "./financial.css";

const REPAYMENT_LABEL = {
  on_track: "On Track",
  completed: "Completed",
  behind: "Behind",
  not_applicable: "N/A",
};

/*
 * Funder — Financial Accountability
 * ---------------------------------
 * Capital deployment, leverage, repayment health and per-cohort breakdown.
 *
 * CSS class prefix for this page: `funderFinancial-`.
 */
export default function Financial() {
  const { user, token } = useAuth();
  const isFunder = user?.role === ROLES.FUNDER;

  const { data: financialApiData, isLoading } = useFunderFinancialHook({
    token,
    userId: user?.id,
    enabled: isFunder,
  });

  const financialData = financialApiData ?? {
    summary_cards: [],
    repayment_status_distribution: [],
    capital_by: {
      sector: [],
      cohort: [],
    },
    cost_efficiency_metrics: [],
  };

  const summaryCards = financialData?.summary_cards ?? [];

  const repaymentData = (
    financialData?.repayment_status_distribution ?? []
  ).map((row, index) => ({
    status: row.status ?? "N/A",
    count: Number(row.value ?? 0),
    fill: CHART_COLORS[index % CHART_COLORS.length],
  }));

  const capitalBySector = (financialData?.capital_by?.sector ?? []).map(
    (row) => ({
      sector:
        SECTOR_LABELS[String(row.sector ?? "").toLowerCase()] ?? row.sector,
      "Her Capital": Number(row.ih_capital ?? 0) / 1_000_000,
      "External Capital": Number(row.external_capital ?? 0) / 1_000_000,
    }),
  );

  const costEfficiency = financialData?.cost_efficiency_metrics ?? [];

  const cohortData = (financialData?.capital_by?.cohort ?? []).map((row) => ({
    cohort: row.period ?? "-",
    capital: Number(row.value ?? 0) / 1_000_000,
  }));

  if (isLoading && isFunder) {
    return (
      <div className="funderFinancial">
        <PageHeader
          title="Financial Accountability"
          description="Capital deployment and repayment metrics."
        />
        <p className="admin-muted">Loading financial data...</p>
      </div>
    );
  }

  return (
    <div className="funderFinancial">
      <PageHeader
        title="Financial Accountability"
        description="Capital deployment and repayment metrics."
      />

      {/* Big stat tiles */}
      <div className="funderFinancial-stats">
        {summaryCards.map((card, index) => {
          const toneByTitle = {
            "Total Her Capital Deployed": "primary",
            "External Capital Leveraged": "accent",
            "Repayment Rate": "success",
          };

          return (
            <BigStat
              key={`${card.title}-${index}`}
              value={card.value}
              label={card.title}
              tone={toneByTitle[card.title] ?? "default"}
            />
          );
        })}
      </div>

      <div className="funderFinancial-grid-2">
        <Card
          title="Repayment Status Distribution"
          description="Portfolio repayment health."
        >
          <div className="funderFinancial-chart">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={repaymentData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="count"
                  label={({ status, count }) => `${status}: ${count}`}
                  labelLine={false}
                />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card
          title="Capital by Sector (UGX M)"
          description=" Her capital vs External capital deployment."
        >
          <div className="funderFinancial-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={capitalBySector}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="sector" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="Her Capital" fill={CHART_COLORS[0]} radius={4} />
                <Bar
                  dataKey="External Capital"
                  fill={CHART_COLORS[1]}
                  radius={4}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <Card
        title="Cost Efficiency Metrics"
        description="Return on capital deployed."
      >
        <div className="funderFinancial-efficiency">
          {costEfficiency.map((metric, index) => (
            <EfficiencyTile
              key={`${metric.metric}-${index}`}
              label={metric.metric}
              value={metric.value}
            />
          ))}
        </div>
      </Card>

      <Card
        title="Capital by Cohort"
        description="Deployment timeline (UGX M)."
      >
        <div className="funderFinancial-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={cohortData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="cohort" />
              <YAxis />
              <Tooltip />
              <Bar
                dataKey="capital"
                name="Capital (M)"
                fill={CHART_COLORS[0]}
                radius={4}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}

function BigStat({ value, label, tone = "default" }) {
  return (
    <div className="funderFinancial-bigStat">
      <p
        className={`funderFinancial-bigStat-value funderFinancial-bigStat-value-${tone}`}
      >
        {value}
      </p>
      <p className="funderFinancial-bigStat-label">{label}</p>
    </div>
  );
}

function EfficiencyTile({ label, value }) {
  return (
    <div className="funderFinancial-effTile">
      <p className="funderFinancial-effTile-label">{label}</p>
      <p className="funderFinancial-effTile-value">{value}</p>
    </div>
  );
}
